<script setup lang="ts">
    import { computed } from 'vue'
    import { useRoute } from 'vue-router'
    import { ProjectPlanListItemReadModel, ProjectPlanListReadModel } from '@Quest/ProjectPlans/readmodels'
    import { useProjectRevenuesQueryV1, useProjectPlanListQueryV1 } from '@Quest/ProjectPlans/queries-vue-composables'

    const props = defineProps<{
        projectName: string

        planId: string
        planName: string
        activePlanId: string | null
    }>()

    const route = useRoute()

    const workspaceId = computed(() => Number(route.params.workspaceId))
    const tchProjectId = computed(() => Number(route.params.tchProjectId))
    const projectId = computed(() => String(route.params.projectId))

    const { runProjectPlanListQueryV1,
        modelProjectPlanListQueryV1,
        running: isLoadingPlans
    } = useProjectPlanListQueryV1(workspaceId, tchProjectId, projectId)

    const plans = computed<ProjectPlanListItemReadModel[] | null>(() => (modelProjectPlanListQueryV1.value as ProjectPlanListReadModel)?.Items ?? null)


    function buildRoute(planId: string)
    {
        return {
            name: route.name!,
            params: {
                ...route.params,
                planId
            },
            query: route.query
        }
    }

    function onOpen()
    {
        if (plans.value == null && isLoadingPlans.value === false)
        {
            runProjectPlanListQueryV1()
        }
    }
</script>

<template>
    <div class="project-context-header">

        <!-- Project name -->
        <h1 class="project-title">
            <a class="project-title-link"
               v-sanitizedHref="`/#/portfolio/alternative/dashboard/${workspaceId}/${tchProjectId}/${projectId}`">

                {{ projectName }}

                <i class="fa fa-external-link-alt project-title-icon"></i>
            </a>
        </h1>

        <!-- Plan selector -->
        <div class="plan-row">
            <div class="dropdown">
                <div class="plan-trigger"
                     @click="onOpen"
                     data-toggle="dropdown">

                    <div class="plan-trigger-content">
                        <span class="plan-name">
                            {{ planName }}
                        </span>

                        <span class="badge"
                              :class="planId === activePlanId ? 'badge-success' : 'badge-secondary'">
                            {{ planId === activePlanId ? 'Active' : 'Inactive' }}
                        </span>
                    </div>

                    <i class="fa fa-chevron-down dropdown-icon"></i>
                </div>

                <div class="dropdown-menu plan-dropdown">

                    <!-- Loading -->
                    <div v-if="isLoadingPlans" class="dropdown-item text-muted loading">
                        <i class="fas fa-spinner fa-spin mr-2"></i>
                        Loading...
                    </div>

                    <!-- Loaded -->
                    <template v-else-if="plans">

                        <div class="plan-list">
                            <router-link v-for="plan in plans"
                                         :key="plan.Id"
                                         class="dropdown-item plan-item"
                                         :class="{ 'active-plan': plan.Id === planId }"
                                         :to="buildRoute(plan.Id)">

                                <div class="plan-item-left">
                                    <span class="plan-item-name">
                                        {{ plan.Name }}
                                    </span>
                                </div>

                                <span v-if="plan.Id === activePlanId" class="badge badge-success">
                                    Active
                                </span>
                            </router-link>
                        </div>

                        <div class="dropdown-divider m-0"></div>

                        <router-link class="dropdown-item manage-link" 
                                     :to="{ 
                                    name: 'q-project-versions-path', 
                                    params: {
                                        workspaceId: workspaceId,
                                        tchProjectId: tchProjectId,
                                        projectId: projectId
                                    },
                                    query: route.query }"> 
                            <i class="fa fa-cog mr-2"></i> Manage business cases 
                        </router-link>

                    </template>

                </div>
            </div>
        </div>
    </div>
</template>


<style scoped>

    /* ===== Layout ===== */

    .project-context-header {
        display: flex;
        flex-direction: column;
        gap: 6px;
    }

    /* ===== Project title ===== */

    .project-title {
        font-size: 1.4em;
        font-weight: 400;
        color: #33475b;
        margin: 0 0 4px 0;
        letter-spacing: 0.01em;
    }

    .project-title-link {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        color: inherit;
        text-decoration: none;
        cursor: pointer;
    }

        .project-title-link:hover {
            color: #2f6fdd;
        }

    .project-title-icon {
        font-size: 0.7em;
        opacity: 0;
        margin-top: 2px;
        transition: opacity 0.15s ease;
    }

    .project-title-link:hover .project-title-icon {
        opacity: 0.6;
    }

    /* ===== Plan row ===== */

    .plan-row {
        display: flex;
        align-items: center;
    }

    /* ===== Trigger ===== */

    .plan-trigger {
        font-size: 0.9rem;
        display: inline-flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        padding: 5px 10px;
        border-radius: 6px;
        background: #fff;
        border: 1px solid #e3e8ef;
        cursor: pointer;
        transition: all 0.15s ease;
        width: fit-content;
        max-width: 420px;
    }

        .plan-trigger:hover {
            background: #eef3f8;
            border-color: #d6dde6;
        }

        .plan-trigger:active {
            background: #e6edf5;
        }

        .plan-trigger:focus-visible {
            outline: 2px solid #2f6fdd;
            outline-offset: 2px;
        }

    /* ===== Inner content ===== */

    .plan-trigger-content {
        display: flex;
        align-items: center;
        gap: 10px;
        min-width: 0; /* enables ellipsis */
    }

    .plan-name {
        font-weight: 600;
        color: #2f3a4a;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    /* ===== Badge ===== */

    .plan-trigger .badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        height: 16px;
        min-width: 58px;
        font-size: 0.65rem;
        padding: 2px 6px;
    }

    .badge-secondary {
        opacity: 0.7; /* softened */
    }

    /* ===== Dropdown ===== */

    .dropdown-icon {
        font-size: 0.7rem;
        opacity: 0.5;
        margin-left: 4px;
    }

    .plan-dropdown {
        min-width: 320px; /* increased */
        padding: 0;
        border-radius: 8px;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
        margin-top: 4px;
    }

    .plan-list {
        max-height: 280px;
        overflow-y: auto;
    }

    /* ===== Items ===== */

    .plan-item {
        display: flex;
        align-items: center;
        padding: 8px 14px; /* tighter */
    }

    .plan-item-left {
        display: flex;
        align-items: center;
        gap: 8px;
        flex: 1; /* allows badge alignment */
    }

    .plan-item-name {
        font-size: 0.95rem;
        color: #2f3a4a;
    }

    .plan-item .badge {
        margin-left: auto; /* better alignment */
    }

    .active-plan {
        background: #eef4ff;
        border-left: 3px solid #2f6fdd;
    }

        .active-plan .plan-item-name {
            font-weight: 500; /* reduced from 600 */
        }

    .plan-item:hover:not(.active-plan) {
        background: #f5f7fa;
    }

    /* ===== Loading ===== */

    .loading {
        display: flex;
        align-items: center;
    }

    /* ===== Footer ===== */

    .manage-link {
        font-weight: 500;
        color: #2f6fdd;
    }
</style>